import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Divider,
  Chip,
} from '@mui/material';
import {
  Analytics,
  Close,
  CheckBox,
  CheckBoxOutlineBlank,
  VideoLibrary,
} from '@mui/icons-material';
import { StyledEmptyState, StyledEmptyStateIcon } from './styles';
import { API_URL } from './config';
import axios from 'axios';

const MAX_ANALYZE = 100;

function BulkAnalyzeModal({
  open,
  onClose,
  videos,
  showTranslation,
  onSuccess,
  onError,
  onRefresh,
}) {
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [bulkAnalyzeProgress, setBulkAnalyzeProgress] = useState({
    completed: 0,
    total: 0,
    errors: [],
  });
  const [bulkAnalyzeJobId, setBulkAnalyzeJobId] = useState(null);
  const [analysisStartTime, setAnalysisStartTime] = useState(null);
  const [showStaleMessage, setShowStaleMessage] = useState(false);

  // Poll job status when a job is active
  useEffect(() => {
    if (!bulkAnalyzeJobId || !bulkAnalyzing) {
      return;
    }

    const pollJobStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/jobs/${bulkAnalyzeJobId}`);
        const job = response.data;

        // Update progress
        setBulkAnalyzeProgress({
          completed: job.completed,
          total: job.total,
          errors: job.errors || [],
        });

        // Check if job is complete
        if (job.status === 'completed' || job.status === 'failed') {
          setBulkAnalyzing(false);

          // Refresh videos to show updated analysis status
          if (onRefresh) {
            onRefresh().catch((err) => {
              console.error('Error refreshing folder data:', err);
            });
          }

          // Show success/error message
          if (job.status === 'completed') {
            const successCount = job.total - (job.errors?.length || 0);
            if (job.errors?.length === 0) {
              if (onSuccess) {
                onSuccess(`Successfully analyzed ${job.total} video(s)!`);
              }
              setTimeout(() => {
                handleClose();
              }, 5000);
            }
          }
        }
      } catch (error) {
        console.error('Error polling job status:', error);
      }
    };

    // Poll immediately, then every 2 seconds
    pollJobStatus();
    const intervalId = setInterval(pollJobStatus, 2000);

    return () => clearInterval(intervalId);
  }, [bulkAnalyzeJobId, bulkAnalyzing, onRefresh, onSuccess, onError]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedVideos(new Set());
      setBulkAnalyzeProgress({ completed: 0, total: 0, errors: [] });
      setBulkAnalyzeJobId(null);
      setBulkAnalyzing(false);
      setAnalysisStartTime(null);
      setShowStaleMessage(false);
    }
  }, [open]);

  // Track analysis start time and show stale message after 60 seconds
  useEffect(() => {
    if (bulkAnalyzing && !analysisStartTime) {
      setAnalysisStartTime(Date.now());
      setShowStaleMessage(false);
    } else if (!bulkAnalyzing) {
      setAnalysisStartTime(null);
      setShowStaleMessage(false);
    }
  }, [bulkAnalyzing, analysisStartTime]);

  // Check if 60 seconds have passed
  useEffect(() => {
    if (!bulkAnalyzing || !analysisStartTime) {
      return;
    }

    const checkStale = () => {
      const elapsed = Date.now() - analysisStartTime;
      if (elapsed >= 60000) {
        setShowStaleMessage(true);
      }
    };

    // Check immediately in case we're already past 60 seconds
    checkStale();

    // Check every second
    const intervalId = setInterval(checkStale, 1000);

    return () => clearInterval(intervalId);
  }, [bulkAnalyzing, analysisStartTime]);

  const handleClose = () => {
    if (!bulkAnalyzing) {
      setSelectedVideos(new Set());
      setBulkAnalyzeProgress({ completed: 0, total: 0, errors: [] });
      setBulkAnalyzeJobId(null);
      onClose();
    }
  };

  const handleVideoToggle = (videoId) => {
    setSelectedVideos((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        // Only allow adding if we haven't reached the limit
        if (newSet.size < MAX_ANALYZE) {
          newSet.add(videoId);
        }
      }
      return newSet;
    });
  };

  const handleSelectFirst30 = () => {
    // Get unanalyzed videos first, then limit to MAX_ANALYZE
    const unanalyzedVideos = videos.filter((v) => !v.hasAnalyzed);
    const firstVideos = unanalyzedVideos.slice(0, MAX_ANALYZE).map((v) => v.videoId);
    const firstVideosSet = new Set(firstVideos);

    // Check if all first videos are already selected
    const allSelected = firstVideos.length > 0 && firstVideos.every((id) => selectedVideos.has(id));

    if (allSelected) {
      // Deselect all
      setSelectedVideos(new Set());
    } else {
      // Select first videos
      setSelectedVideos(firstVideosSet);
    }
  };

  const handleBulkAnalyzeSubmit = async () => {
    if (selectedVideos.size === 0) {
      return;
    }

    // Limit to maximum 100 videos
    const videoIds = Array.from(selectedVideos).slice(0, MAX_ANALYZE);

    // If more than 100 were selected, update the selection
    if (selectedVideos.size > MAX_ANALYZE) {
      setSelectedVideos(new Set(videoIds));
    }

    setBulkAnalyzing(true);
    setBulkAnalyzeProgress({ completed: 0, total: videoIds.length, errors: [] });

    try {
      // Schedule the bulk analyze job
      const response = await axios.post(`${API_URL}/api/videos/bulk-analyze`, {
        videoIds,
      });

      setBulkAnalyzeJobId(response.data.jobId);
      setBulkAnalyzeProgress({ completed: 0, total: response.data.total, errors: [] });
    } catch (error) {
      console.error('Error scheduling bulk analyze job:', error);
      if (onError) {
        onError(error.message || 'Failed to schedule bulk analyze job');
      }
      setBulkAnalyzing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background:
            'linear-gradient(145deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
          border: '1px solid rgba(148, 163, 184, 0.1)',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Analytics />
            <Typography variant="h6">Bulk Analyze Videos</Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={bulkAnalyzing}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                background: 'rgba(148, 163, 184, 0.1)',
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              Select videos to analyze ({selectedVideos.size} of {MAX_ANALYZE} selected)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {(() => {
                const unanalyzedVideos = videos.filter((v) => !v.hasAnalyzed);
                const firstVideos = unanalyzedVideos.slice(0, MAX_ANALYZE).map((v) => v.videoId);
                const allSelected =
                  firstVideos.length > 0 && firstVideos.every((id) => selectedVideos.has(id));
                return (
                  <Button
                    size="small"
                    onClick={handleSelectFirst30}
                    disabled={bulkAnalyzing || videos.filter((v) => !v.hasAnalyzed).length === 0}
                    startIcon={allSelected ? <CheckBox /> : <CheckBoxOutlineBlank />}
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      '&:hover': {
                        color: 'text.primary',
                        background: 'rgba(148, 163, 184, 0.1)',
                      },
                    }}
                  >
                    {allSelected ? `Deselect First ${MAX_ANALYZE}` : `Select First ${MAX_ANALYZE}`}
                  </Button>
                );
              })()}
            </Box>
          </Box>
          {selectedVideos.size > MAX_ANALYZE && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                Maximum {MAX_ANALYZE} videos can be analyzed at once. Only the first {MAX_ANALYZE}{' '}
                will be processed.
              </Typography>
            </Alert>
          )}
        </Box>

        {bulkAnalyzing && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <CircularProgress size={20} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                Analyzing {bulkAnalyzeProgress.completed + bulkAnalyzeProgress.errors.length} of{' '}
                {bulkAnalyzeProgress.total} videos... After this, you can view the analysis by
                clicking on the <Analytics sx={{ fontSize: 16, color: '#10b981' }} /> button on each
                video
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 8,
                background: 'rgba(148, 163, 184, 0.1)',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${
                    bulkAnalyzeProgress.total > 0
                      ? (bulkAnalyzeProgress.completed / bulkAnalyzeProgress.total) * 100
                      : 0
                  }%`,
                  height: '100%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
            {showStaleMessage && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: '#f97316',
                  fontWeight: 500,
                }}
              >
                Note: If the progress bar has been stale for more than 1 minute, refresh the page.
                Your analysis is complete
              </Typography>
            )}
          </Box>
        )}

        {bulkAnalyzeProgress.errors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              {bulkAnalyzeProgress.errors.length} video(s) failed to analyze. These videos might
              have been removed from YouTube, or do not have a transcript.
            </Typography>
          </Alert>
        )}

        <List sx={{ py: 0 }}>
          {videos.map((video, index) => (
            <React.Fragment key={video.videoId || video._id}>
              <ListItem
                button={!video.hasAnalyzed}
                onClick={() =>
                  !bulkAnalyzing && !video.hasAnalyzed && handleVideoToggle(video.videoId)
                }
                disabled={bulkAnalyzing || video.hasAnalyzed}
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 2,
                  mb: 0.5,
                  background: video.hasAnalyzed
                    ? 'rgba(16, 185, 129, 0.05)'
                    : selectedVideos.has(video.videoId)
                      ? 'rgba(16, 185, 129, 0.1)'
                      : 'rgba(148, 163, 184, 0.05)',
                  cursor: bulkAnalyzing || video.hasAnalyzed ? 'default' : 'pointer',
                  '&:hover': {
                    background:
                      bulkAnalyzing || video.hasAnalyzed
                        ? undefined
                        : selectedVideos.has(video.videoId)
                          ? 'rgba(16, 185, 129, 0.15)'
                          : 'rgba(148, 163, 184, 0.15)',
                  },
                  transition: 'background-color 0.2s ease-in-out',
                  opacity: bulkAnalyzing || video.hasAnalyzed ? 0.6 : 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  {video.hasAnalyzed ? (
                    <CheckBox sx={{ color: '#10b981', opacity: 0.5 }} />
                  ) : selectedVideos.has(video.videoId) ? (
                    <CheckBox sx={{ color: '#10b981' }} />
                  ) : (
                    <CheckBoxOutlineBlank sx={{ color: 'text.secondary' }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {showTranslation && video.translation ? video.translation : video.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {video.channelName || 'Unknown Channel'} • {video.viewCount.toLocaleString()}{' '}
                      views
                      {video.hasAnalyzed && (
                        <Chip
                          label="Analyzed"
                          size="small"
                          sx={{
                            ml: 1,
                            height: 16,
                            fontSize: '0.65rem',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                          }}
                        />
                      )}
                    </Typography>
                  </Box>
                </Box>
              </ListItem>
              {index < videos.length - 1 && <Divider sx={{ my: 0.5 }} />}
            </React.Fragment>
          ))}
        </List>

        {videos.length === 0 && (
          <StyledEmptyState>
            <StyledEmptyStateIcon>
              <VideoLibrary sx={{ fontSize: 40, color: 'text.secondary' }} />
            </StyledEmptyStateIcon>
            <Typography variant="h6" color="text.secondary" mb={1}>
              No videos available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add channels and wait for videos to be fetched
            </Typography>
          </StyledEmptyState>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={bulkAnalyzing}>
          {bulkAnalyzing ? 'Processing...' : 'Cancel'}
        </Button>
        <Button
          onClick={handleBulkAnalyzeSubmit}
          variant="contained"
          disabled={bulkAnalyzing || selectedVideos.size === 0}
          startIcon={bulkAnalyzing ? <CircularProgress size={20} color="inherit" /> : <Analytics />}
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              opacity: 0.9,
            },
          }}
        >
          {bulkAnalyzing
            ? `Analyzing... (${bulkAnalyzeProgress.completed + bulkAnalyzeProgress.errors.length}/${bulkAnalyzeProgress.total})`
            : `Analyze ${Math.min(selectedVideos.size, MAX_ANALYZE)} Video(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BulkAnalyzeModal;
